/**
 * المرحلة 1.35 (R6–R10) — the quick reception: the centre's paper voucher
 * «إيصال تحصيل نقدية» as one card, top to bottom as the paper reads.
 *
 *   the number      the next one in red (the paper's «№»), the date and hour
 *   مريض قديم؟      one box for a whole mobile, national ID or insurance
 *                   number; a match fills the card
 *   who             «وصل من السيد/ة» (the guardian, with أب / أم / ولي أمر),
 *                   the child's (or patient's) name with the policy's name
 *                   count, and one identifier: the guardian's mobile for a
 *                   child, «أو» the national ID or the insurance number
 *   وذلك نظير       the price list's dropdowns: a category, its service, the
 *                   service's detail; the last pick adds a line, with a count
 *                   for days, hours and sessions
 *   the money       the total in the paper's two boxes and in words, «خالص»
 *                   or «آجل» with what is paid now, and «المتسلم»: the
 *                   signed-in account, read-only
 *
 *   «خصم»           a named discount the manager or accounts wrote, on a
 *                   line or on the total, before the final price — and the
 *                   desk writes why, every time (domain/discounts.js)
 *
 * «اطبع الإيصال» issues and prints the A4 paper; «ابعته واتساب» issues and
 * opens WhatsApp Web on the guardian's chat with the soft copy's message
 * written; once issued, the ready link stays on the card to tap again. The
 * server prices every line from the list, takes the discounts off, opens or
 * finds the file and numbers the voucher in one transaction
 * (Backend/routes/quickDesk.js). «مرتجع» hands services back on the voucher
 * just issued (RefundDialog). «بيانات إضافية» holds the rest of the file,
 * collapsed and never required.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, DateInput, ErrorState, Field, Icon, Input, Select, Spinner } from '../../components/common';
import { quickDeskApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { usePermissions } from '../../context/PermissionContext';
import { cascade } from '../../domain/priceCascade';
import { ID_KINDS, RELATIONS, addLine, balanceOf, counts, emptyCard, missing, payloadOf, pricedCard, sheetOf, wordsFor } from '../../domain/voucher';
import { labelOf, reaches } from '../../domain/discounts';
import { mayRefund } from '../../domain/refund';
import { whatsappHref } from '../../domain/whatsapp';
import { WhatsAppLink } from '../../components/common/WhatsAppLink';
import { formatMoney } from '../../domain/locale';
import { pickLogo } from '../../domain/logoBackdrop';
import { isDarkTheme } from '../../domain/themes';
import { useTheme } from '../../context/ThemeContext';
import { useI18n } from '../../i18n/I18nProvider';
import { printArea } from '../hospitalShared/labels';
import { VoucherSheet } from './VoucherSheet';
import { RefundDialog } from './RefundDialog';

const pad = (n) => String(n).padStart(2, '0');

/** The server's clock, ticking: the offset is taken once from the options' `now`. */
function useServerClock(serverNow) {
  const offset = useMemo(() => (serverNow ? Date.parse(serverNow) - Date.now() : 0), [serverNow]);
  const [now, setNow] = useState(() => new Date(Date.now() + offset));
  useEffect(() => {
    setNow(new Date(Date.now() + offset));
    const timer = setInterval(() => setNow(new Date(Date.now() + offset)), 15000);
    return () => clearInterval(timer);
  }, [offset]);
  return now;
}

export function QuickReception({ onOpenFile }) {
  const { t } = useI18n();
  const { success, error: toastError } = useNotifications();
  const { institutionName, institutionLogo, institutionLogoDark, institutionLogoBackdrop, can } = usePermissions();
  const { theme } = useTheme();
  const cardLogo = pickLogo({ logo: institutionLogo, logoDark: institutionLogoDark, backdrop: institutionLogoBackdrop }, { dark: isDarkTheme(theme) });
  const [opts, setOpts] = useState({ loading: true, error: null, data: null });
  const [card, setCard] = useState(null);
  const [picks, setPicks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);
  const [link, setLink] = useState(null);
  const [taken, setTaken] = useState(null);
  const [printWanted, setPrintWanted] = useState(false);
  const [offOpen, setOffOpen] = useState(() => new Set());
  const [totalOpen, setTotalOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const sheetRef = useRef(null);
  // One soft-copy link per voucher: a second one would revoke the first.
  const minting = useRef(null);

  const load = useCallback(
    (quiet = false) =>
      quickDeskApi
        .options()
        .then((data) => {
          setOpts({ loading: false, error: null, data });
          setCard((c) => c || emptyCard(data.policy.quickReception));
        })
        .catch((error) => !quiet && setOpts({ loading: false, error, data: null })),
    [],
  );
  useEffect(() => {
    load();
  }, [load]);

  const data = opts.data;
  const policy = data ? data.policy.quickReception : null;
  const leaves = useMemo(() => (data ? data.leaves : []), [data]);
  const currency = data && data.country ? data.country.currency : 'EGP';
  const now = useServerClock(data && data.now);
  const discounts = useMemo(() => (data && Array.isArray(data.discounts) ? data.discounts : []), [data]);
  const invoiceOffers = discounts.filter((d) => d.target === 'invoice');
  const offersFor = (leaf) => discounts.filter((d) => reaches(d, leaf));
  const preview = useMemo(() => pricedCard(leaves, card, discounts), [leaves, card, discounts]);
  const balance = card ? balanceOf(card, preview.total) : { paid: 0, remaining: 0 };
  const words = wordsFor(preview.total, currency);
  const blockers = card && policy ? missing(card, { nameParts: policy.nameParts, leaves }) : [];
  const done = Boolean(issued);

  const ensureLink = useCallback((receiptId) => {
    if (minting.current && minting.current.receiptId === receiptId) return minting.current.promise;
    const promise = quickDeskApi.link(receiptId).then((made) => {
      const ready = { ...made, receiptId };
      setLink(ready);
      return ready;
    });
    minting.current = { receiptId, promise };
    promise.catch(() => {
      if (minting.current && minting.current.receiptId === receiptId) minting.current = null;
    });
    return promise;
  }, []);

  // Issued: the ready WhatsApp link, minted once, waits on the card.
  useEffect(() => {
    if (!issued || !policy || policy.softCopy !== 'link') return;
    ensureLink(issued.id).catch(() => {});
  }, [issued, policy, ensureLink]);

  // The paper prints once it is drawn from what the server saved.
  useEffect(() => {
    if (!printWanted || !issued || !sheetRef.current) return;
    setPrintWanted(false);
    printArea(sheetRef.current, { title: `${t('quick.title')} ${issued.receipt.number}` });
  }, [printWanted, issued, t]);

  const set = (patch) => setCard((c) => ({ ...c, ...patch }));
  const setExtra = (key) => (e) => setCard((c) => ({ ...c, extra: { ...c.extra, [key]: e.target.value } }));

  const pick = (level, value) => {
    const next = [...picks.slice(0, level), value];
    const { leaf } = cascade(leaves, next);
    if (leaf) {
      set({ lines: addLine(card.lines, leaf) });
      setPicks([]);
    } else setPicks(next);
  };

  const issue = async () => {
    setBusy(true);
    setTaken(null);
    try {
      const out = await quickDeskApi.issue(payloadOf(card, leaves));
      setIssued(out);
      success(t('quick.issued', { number: out.receipt.number }));
      load(true);
      return out;
    } catch (err) {
      if (err.body && err.body.code === 'IDENTIFIER_TAKEN' && err.body.existing) setTaken(err.body.existing);
      toastError(err.message);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const printNow = async () => {
    const out = issued || (await issue());
    if (out) setPrintWanted(true);
  };

  const sendWhatsApp = async () => {
    // Opened in the click itself, so the browser does not block it — the
    // same named tab every time, so the desk's WhatsApp Web stays one tab.
    const win = window.open('', 'capsule-whatsapp');
    const out = issued || (await issue());
    if (!out) {
      if (win) win.close();
      return;
    }
    try {
      const made = await ensureLink(out.id);
      const href = whatsappHref(made.whatsappUrl);
      if (win) win.location.href = href;
      else window.location.assign(href);
    } catch (err) {
      if (win) win.close();
      toastError(err.message);
    }
  };

  const fresh = () => {
    setIssued(null);
    setLink(null);
    minting.current = null;
    setTaken(null);
    setPicks([]);
    setOffOpen(new Set());
    setTotalOpen(false);
    setCard(emptyCard(policy));
  };

  const setLine = (key, patch) => set({ lines: card.lines.map((x) => (x.key === key ? { ...x, ...patch } : x)) });
  // «خصم» on a line opens its box; tapped again, the discount comes off.
  const toggleOff = (key) => {
    const line = card.lines.find((x) => x.key === key);
    if (offOpen.has(key) || (line && line.discountId)) {
      setLine(key, { discountId: '', discountReason: '' });
      setOffOpen((open) => new Set([...open].filter((k) => k !== key)));
    } else setOffOpen((open) => new Set([...open, key]));
  };
  // «خصم على الإجمالي» the same way, from a chip beside the pickers: the card keeps to one screen until it is used.
  const toggleTotal = () => {
    if (totalOpen || card.discount.discountId) {
      set({ discount: { ...card.discount, discountId: '', reason: '' } });
      setTotalOpen(false);
    } else setTotalOpen(true);
  };

  if (opts.loading && !data) return <Spinner label={t('quick.loading')} />;
  if (opts.error && !data) return <ErrorState error={opts.error} onRetry={() => load()} />;
  if (!card) return null;

  const levels = cascade(leaves, picks).levels;
  const totalOffers = !done && policy.discounts === 'named' && invoiceOffers.length > 0;
  const totalShown = totalOffers && preview.lines.length > 0 && (totalOpen || Boolean(card.discount.discountId));
  const idLabel = card.idKind === 'mobile' ? (card.isMinor ? t('quick.guardianMobile') : t('quick.mobile')) : t(`quick.idKind.${card.idKind}`);
  const sheet = issued
    ? sheetOf(issued.receipt, { institution: { name: institutionName, logo: institutionLogo, logoBackdrop: institutionLogoBackdrop }, currency })
    : null;

  return (
    <div className="qk">
      <section className={`qk-card${done ? ' is-done' : ''}`} aria-label={t('quick.title')}>
        {/* 1 — the head: the logo, the title box, the number in red, the clock */}
        <header className="qk-head">
          <div className="qk-org">
            {cardLogo.src && <img src={cardLogo.src} alt="" className={`qk-logo ${cardLogo.plate}`} />}
            <span className="qk-org-name">{institutionName}</span>
          </div>
          <h2 className="qk-title">{t('quick.title')}</h2>
          <div className="qk-no">
            <span className="qk-no-row">
              <span>№</span>
              <b dir="ltr">{done ? issued.receipt.number : data.nextNumber}</b>
            </span>
            <span className="qk-clock">
              <span>
                {t('quick.date')} <b dir="ltr">{`${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()}`}</b>
              </span>
              <span>
                {t('quick.time')} <b dir="ltr">{`${pad(now.getHours())}:${pad(now.getMinutes())}`}</b>
              </span>
            </span>
          </div>
        </header>

        {/* 2 — مريض قديم؟ and whether it is a child, on one row */}
        <div className="qk-bar">
          {!done && (
            <FindFile
              card={card}
              onPick={(file, typed) =>
                set({
                  fileId: file.id,
                  found: file,
                  fullName: file.fullName,
                  isMinor: file.isMinor,
                  guardianName: file.guardianName || '',
                  guardianRelation: file.guardianRelation || 'father',
                  idValue: typed,
                })
              }
              onClear={() => set({ fileId: null, found: null })}
            />
          )}
          <div className="qk-kind" role="group" aria-label={t('quick.kind')}>
            {[true, false].map((minor) => (
              <button
                key={String(minor)}
                type="button"
                className={`qk-chip${card.isMinor === minor ? ' is-on' : ''}`}
                aria-pressed={card.isMinor === minor}
                disabled={done || Boolean(card.fileId)}
                onClick={() => set({ isMinor: minor })}
              >
                <Icon name={minor ? 'baby' : 'user'} size={14} />
                {minor ? t('quick.child') : t('quick.adult')}
              </button>
            ))}
          </div>
        </div>

        {/* 3–5 — who pays, who it is for, the one identifier */}
        <div className="qk-who">
          {card.isMinor && (
            <Field label={t('quick.payer')} className="qk-field qk-field--payer">
              <div className="qk-payer">
                <Input value={card.guardianName} onChange={(e) => set({ guardianName: e.target.value })} disabled={done || Boolean(card.fileId)} placeholder={t('quick.payerHint')} />
                <div className="qk-rel" role="group" aria-label={t('quick.relation')}>
                  {RELATIONS.map((r) => (
                    <button key={r} type="button" className={`qk-chip${card.guardianRelation === r ? ' is-on' : ''}`} aria-pressed={card.guardianRelation === r} disabled={done || Boolean(card.fileId)} onClick={() => set({ guardianRelation: r })}>
                      {t(`quick.rel.${r}`)}
                    </button>
                  ))}
                </div>
              </div>
            </Field>
          )}

          <Field label={card.isMinor ? t('quick.childName') : t('quick.patientName')} className="qk-field">
            <Input value={card.fullName} onChange={(e) => set({ fullName: e.target.value })} disabled={done || Boolean(card.fileId)} placeholder={t('quick.nameHint', { n: policy.nameParts })} />
          </Field>

          <Field label={idLabel} className="qk-field">
            <div className="qk-id">
              <Input
                value={card.idValue}
                onChange={(e) => set({ idValue: e.target.value })}
                disabled={done || Boolean(card.fileId)}
                inputMode={card.idKind === 'insurance' ? 'text' : 'numeric'}
                dir="ltr"
              />
              <Select aria-label={t('quick.or')} value={card.idKind} onChange={(e) => set({ idKind: e.target.value })} disabled={done || Boolean(card.fileId)} className="qk-id-kind">
                {ID_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k === 'mobile' ? t('quick.idKind.mobile') : `${t('quick.or')} ${t(`quick.idKind.${k}`)}`}
                  </option>
                ))}
              </Select>
            </div>
          </Field>
        </div>

        {taken && (
          <p className="qk-taken">
            <Icon name="alert-triangle" size={14} />
            {t('quick.taken', { name: taken.fullName })}
            <Button variant="subtle" icon="folder-open" onClick={() => { set({ fileId: taken.id, found: taken, fullName: taken.fullName }); setTaken(null); }}>
              {t('quick.useFile')}
            </Button>
          </p>
        )}

        {/* 6 — وذلك نظير */}
        <div className="qk-for">
          <span className="qk-label">{t('quick.for')}</span>
          {!done && (
            <div className="qk-pickers">
              {levels.map((lv) => (
                <Select key={lv.level} aria-label={t(`priceList.level.${lv.level + 1}`)} value={lv.value === null ? '' : String(lv.value)} onChange={(e) => e.target.value && pick(lv.level, e.target.value)}>
                  <option value="">{t(`quick.pick.${lv.level + 1}`)}</option>
                  {lv.options.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.leaf ? `${o.name} — ${formatMoney(o.leaf.price)}` : o.name}
                    </option>
                  ))}
                </Select>
              ))}
            </div>
          )}
          {totalOffers && (
            <button
              type="button"
              className={`qk-off-btn qk-off-btn--total${totalShown ? ' is-on' : ''}`}
              aria-pressed={totalShown}
              disabled={preview.lines.length === 0}
              onClick={toggleTotal}
              data-testid="invoice-discount-toggle"
            >
              <Icon name="percent" size={13} />
              {t('quick.off.invoice')}
            </button>
          )}
          {preview.lines.length > 0 && (
            <ul className="qk-lines">
              {preview.lines.map((l) => {
                const offers = policy.discounts === 'named' ? offersFor(l.leaf) : [];
                const offShown = offers.length > 0 && (offOpen.has(l.key) || Boolean(l.discountId));
                return (
                <li key={l.key} className={`qk-line${offShown ? ' has-off' : ''}`} style={{ '--plist-tint': l.leaf.color || '#64748b' }}>
                  <span className="qk-line-name">{l.leaf.path.join(' — ')}</span>
                  {counts(l.leaf.unit) && (
                    <span className="qk-qty">
                      <Input
                        aria-label={t('quick.quantity')}
                        value={card.lines.find((x) => x.key === l.key).quantity}
                        onChange={(e) => set({ lines: card.lines.map((x) => (x.key === l.key ? { ...x, quantity: e.target.value } : x)) })}
                        inputMode="decimal"
                        dir="ltr"
                        disabled={done}
                      />
                      <span>{t(`priceList.units.${l.leaf.unit}`)}</span>
                    </span>
                  )}
                  {offers.length > 0 && !done && (
                    <button type="button" className={`qk-off-btn${offShown ? ' is-on' : ''}`} aria-pressed={offShown} onClick={() => toggleOff(l.key)} data-testid="line-discount">
                      <Icon name="percent" size={13} />
                      {t('quick.off.add')}
                    </button>
                  )}
                  <b className="qk-line-amount">
                    {l.off > 0 && <s className="qk-gross">{formatMoney(l.gross)}</s>}
                    {formatMoney(l.amount)}
                  </b>
                  {!done && (
                    <button type="button" className="qk-x" aria-label={t('quick.remove')} onClick={() => set({ lines: card.lines.filter((x) => x.key !== l.key) })}>
                      <Icon name="x" size={14} />
                    </button>
                  )}
                  {offShown && (
                    <div className="qk-off" data-testid="line-discount-box">
                      <Select aria-label={t('quick.off.pick')} value={l.discountId ? String(l.discountId) : ''} onChange={(e) => setLine(l.key, { discountId: e.target.value })} disabled={done} data-testid="line-discount-pick">
                        <option value="">{t('quick.off.pick')}</option>
                        {offers.map((d) => (
                          <option key={d.id} value={d.id}>
                            {labelOf(d)}
                          </option>
                        ))}
                      </Select>
                      {l.discountId && (
                        <Input
                          aria-label={t('quick.off.reason')}
                          value={l.discountReason || ''}
                          onChange={(e) => setLine(l.key, { discountReason: e.target.value })}
                          placeholder={t('quick.off.reasonHint')}
                          disabled={done}
                          data-testid="line-discount-reason"
                        />
                      )}
                      {l.off > 0 && <b className="qk-off-amount">−{formatMoney(l.off)}</b>}
                    </div>
                  )}
                </li>
                );
              })}
            </ul>
          )}
          {totalShown && (
            <div className={`qk-off qk-off--total${card.discount.discountId ? ' is-on' : ''}`} data-testid="invoice-discount">
              <span className="qk-label">
                <Icon name="percent" size={13} /> {t('quick.off.invoice')}
              </span>
              <Select aria-label={t('quick.off.invoice')} value={card.discount.discountId ? String(card.discount.discountId) : ''} onChange={(e) => set({ discount: { ...card.discount, discountId: e.target.value } })} data-testid="invoice-discount-pick">
                <option value="">{t('quick.off.none')}</option>
                {invoiceOffers.map((d) => (
                  <option key={d.id} value={d.id}>
                    {labelOf(d)}
                  </option>
                ))}
              </Select>
              {card.discount.discountId && (
                <Input
                  aria-label={t('quick.off.reason')}
                  value={card.discount.reason}
                  onChange={(e) => set({ discount: { ...card.discount, reason: e.target.value } })}
                  placeholder={t('quick.off.reasonHint')}
                  data-testid="invoice-discount-reason"
                />
              )}
              {preview.invoice && preview.invoice.amount > 0 && <b className="qk-off-amount">−{formatMoney(preview.invoice.amount)}</b>}
            </div>
          )}
          {preview.discountTotal > 0 && (
            <p className="qk-net" data-testid="discount-summary">
              <span>
                {t('quick.off.subtotal')} <b>{formatMoney(preview.subtotal)}</b>
              </span>
              <span className="qk-net-off">
                {t('quick.off.total')} <b>−{formatMoney(preview.discountTotal)}</b>
              </span>
              <span className="qk-net-total">
                {t('quick.off.net')} <b>{formatMoney(preview.total)}</b>
              </span>
            </p>
          )}
        </div>

        {/* 7–11 — the money, paid or deferred, and who received it */}
        <div className="qk-money">
          <div className="qk-amount">
            <table className="qk-boxes">
              <thead>
                <tr>
                  <th>{words.boxes[0]}</th>
                  <th>{words.boxes[1]}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td dir="ltr">{words.major.toLocaleString('en-US')}</td>
                  <td dir="ltr">{pad(words.minor)}</td>
                </tr>
              </tbody>
            </table>
            <p className="qk-words">
              <span className="qk-label">{t('quick.words')}</span> {preview.total > 0 ? words.words : '—'}
            </p>
          </div>

          <div className="qk-pay">
            <div className="qk-paytype" role="group" aria-label={t('quick.payType')}>
              {['cash', 'deferred'].map((type) => (
                <button
                  key={type}
                  type="button"
                  className={`qk-paybtn qk-paybtn--${type}${card.receiptType === type ? ' is-on' : ''}`}
                  aria-pressed={card.receiptType === type}
                  disabled={done || (type === 'deferred' && !data.policy.deferred.allowed)}
                  onClick={() => set({ receiptType: type })}
                >
                  {t(`quick.${type}`)}
                </button>
              ))}
            </div>
            {card.receiptType === 'deferred' && (
              <div className="qk-deferred">
                <Field label={t('quick.paidNow')}>
                  <Input value={card.paidAmount} onChange={(e) => set({ paidAmount: e.target.value })} inputMode="decimal" dir="ltr" disabled={done} />
                </Field>
                <span className="qk-remaining">
                  {t('quick.remaining')} <b>{formatMoney(balance.remaining)}</b>
                </span>
              </div>
            )}
          </div>

          <div className="qk-sign">
            <span className="qk-label">{t('quick.receiver')}</span>
            <b>{data.signer.name}</b>
            <span className="qk-sign-title">{data.signer.title}</span>
          </div>
        </div>

        {/* The two big buttons */}
        <div className="qk-actions">
          {done ? (
            <>
              <Button icon="printer" onClick={() => setPrintWanted(true)}>
                {t('quick.printAgain')}
              </Button>
              {policy.softCopy === 'link' && (
                <Button variant="success" icon="message-circle" loading={busy} onClick={sendWhatsApp}>
                  {t('quick.whatsapp')}
                </Button>
              )}
              {mayRefund(policy, can) && (
                <Button variant="danger" icon="undo-2" onClick={() => setRefundOpen(true)} data-testid="quick-refund">
                  {t('refund.open')}
                </Button>
              )}
              <Button variant="ghost" icon="plus" onClick={fresh}>
                {t('quick.next')}
              </Button>
              {issued.patient && onOpenFile && (
                <Button variant="subtle" icon="folder-open" onClick={() => onOpenFile(issued.patient.id)}>
                  {t('quick.openFile')}
                </Button>
              )}
            </>
          ) : (
            <>
              <Button icon="printer" loading={busy} disabled={blockers.length > 0} onClick={printNow}>
                {t('quick.print')}
              </Button>
              {policy.softCopy === 'link' && (
                <Button variant="success" icon="message-circle" loading={busy} disabled={blockers.length > 0 || (!card.fileId && card.idKind !== 'mobile')} onClick={sendWhatsApp}>
                  {t('quick.whatsapp')}
                </Button>
              )}
              {blockers.length > 0 && <span className="qk-blockers">{t(`quick.missing.${blockers[0]}`, { n: policy.nameParts })}</span>}
            </>
          )}
        </div>
        {done && link && link.receiptId === issued.id && (
          <WhatsAppLink url={link.whatsappUrl} label={t('whatsapp.voucher')} preview testId="quick-whatsapp-link" className="qk-wa" />
        )}
      </section>
      {done && (
        <RefundDialog
          receipt={issued.receipt}
          open={refundOpen}
          onClose={() => setRefundOpen(false)}
          onDone={(receipt) => {
            setIssued((prev) => ({ ...prev, receipt }));
            setRefundOpen(false);
          }}
        />
      )}

      {/* «بيانات إضافية»: the rest of the file, never required */}
      {!done && !card.fileId && (
        <details className="qk-extra">
          <summary>
            <Icon name="chevron-down" size={14} />
            {t('quick.extra')}
          </summary>
          <div className="qk-extra-grid">
            <Field label={t('quick.dob')}>
              <DateInput value={card.extra.dateOfBirth || ''} onChange={setExtra('dateOfBirth')} />
            </Field>
            <Field label={t('quick.gender')}>
              <Select value={card.extra.gender || ''} onChange={setExtra('gender')}>
                <option value="">—</option>
                <option value="male">{t('quick.male')}</option>
                <option value="female">{t('quick.female')}</option>
              </Select>
            </Field>
            <Field label={t('quick.address')}>
              <Input value={card.extra.address || ''} onChange={setExtra('address')} />
            </Field>
            <Field label={t('quick.motherName')}>
              <Input value={card.extra.motherName || ''} onChange={setExtra('motherName')} />
            </Field>
            {card.isMinor && (
              <Field label={t('quick.guardianNationalId')}>
                <Input value={card.extra.guardianNationalId || ''} onChange={setExtra('guardianNationalId')} inputMode="numeric" dir="ltr" />
              </Field>
            )}
            <Field label={t('quick.insurance')}>
              <Input value={card.extra.insuranceProvider || ''} onChange={setExtra('insuranceProvider')} />
            </Field>
          </div>
        </details>
      )}
      {!done && card.fileId && onOpenFile && (
        <Button variant="subtle" icon="folder-open" className="qk-open" onClick={() => onOpenFile(card.fileId)}>
          {t('quick.openFile')}
        </Button>
      )}

      {/* The paper, drawn from what the server saved, for printArea. */}
      {sheet && (
        <div className="qk-paper" aria-hidden="true">
          <VoucherSheet ref={sheetRef} sheet={sheet} qrSvg={link && link.qrSvg} />
        </div>
      )}
    </div>
  );
}

/** «مريض قديم؟»: one whole number finds the file, and a match fills the card. */
function FindFile({ card, onPick, onClear }) {
  const { t } = useI18n();
  const { error: toastError } = useNotifications();
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      setRows((await quickDeskApi.find(q.trim())).files || []);
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (card.fileId && card.found) {
    return (
      <div className="qk-find is-found">
        <Icon name="folder-check" size={16} />
        <span>
          {t('quick.foundFile')} <b>{card.found.fullName}</b>
          {card.found.lastReceipt && <small> — {t('quick.lastReceipt', { number: card.found.lastReceipt.number })}</small>}
        </span>
        <Button variant="subtle" icon="x" onClick={onClear}>
          {t('quick.newFile')}
        </Button>
      </div>
    );
  }

  return (
    <div className="qk-find">
      <span className="qk-label">{t('quick.returning')}</span>
      <Input aria-label={t('quick.findHint')} placeholder={t('quick.findHint')} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && run()} dir="ltr" inputMode="numeric" />
      <Button variant="subtle" icon="search" loading={busy} onClick={run}>
        {t('quick.find')}
      </Button>
      {rows && (
        <div className="qk-found" role="listbox" aria-label={t('quick.returning')}>
          {rows.length === 0 ? (
            <p className="qk-none">{t('quick.notFound')}</p>
          ) : (
            rows.map((f) => (
              <button
                key={f.id}
                type="button"
                role="option"
                aria-selected="false"
                className="qk-found-row"
                onClick={() => {
                  onPick(f, q.trim());
                  setRows(null);
                }}
              >
                <Icon name={f.isMinor ? 'baby' : 'user'} size={14} />
                <b>{f.fullName}</b>
                {f.guardianName && <span>{f.guardianName}</span>}
                {f.mobileTail && <span dir="ltr">{f.mobileTail}</span>}
                {f.lastReceipt && <small>{t('quick.lastReceipt', { number: f.lastReceipt.number })}</small>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default QuickReception;
